/**
 * The Planning helper (D-1019-PLAN): the 14-day spacing rule, the "good next days" for a tenant, and "does the month
 * fit". Pure functions over civil day keys (yyyyMMdd) — the page and the API tests run the same ones. Nothing here
 * writes or refuses: the spacing rule is guidance, and the scheduler can always go ahead with a second press.
 *
 * Eli, 19 Oct 2026: tenants are flexible — there are no fixed A1/A2 weeks; the system watches the gap and guides.
 */
import { addDaysToKey, dayOfWeek, isWeekdayKey, keyToNoonUtc } from './dates';
import { isMissedStatus } from './needs-1104';
import { positionCount } from './booking';

/** A twice-a-month tenant's two visits sit at least this many days apart; under this is "too close" (Eli, 20 Oct 2026). */
export const MIN_GAP_DAYS = 10;
/** The aim: the same weekday two weeks later. Days are ordered by closeness to it. */
export const TARGET_GAP_DAYS = 14;
/** The furthest the generator looks for a second visit before it relaxes the window (10 to 18 days). */
export const MAX_WINDOW_GAP_DAYS = 18;

/** Whole days from `a` to `b` (negative when b is earlier). Civil days, so no clock or time zone enters. */
export function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(keyToNoonUtc(b)) - Date.parse(keyToNoonUtc(a))) / 86_400_000);
}

/** A visit of the tenant's — booked, confirmed or in pencil, this month or the one before or after. */
export interface OtherVisit {
  dayKey: string;
  appointmentId?: string;
}

export type Spacing =
  | { ok: true; /** The nearest other visit, when there is one. */ nearest: { dayKey: string; gap: number } | null }
  | { ok: false; tooClose: true; other: string; gap: number };

/**
 * The nearest other visit to `candidate` and the gap in days. `ok` when the gap is 10 or more, or when the tenant comes
 * once a month (`twicePerMonth` false) — then there is nothing to space. `excludeId` leaves out the visit being moved.
 * A visit on the very day (gap 0) is too close for a twice-a-month tenant.
 */
export function spacingOf(candidate: string, others: readonly OtherVisit[], twicePerMonth: boolean, excludeId?: string): Spacing {
  let best: { dayKey: string; gap: number } | null = null;
  for (const o of others) {
    if (excludeId && o.appointmentId === excludeId) continue;
    const gap = Math.abs(daysBetween(o.dayKey, candidate));
    if (!best || gap < best.gap || (gap === best.gap && o.dayKey < best.dayKey)) best = { dayKey: o.dayKey, gap };
  }
  if (!twicePerMonth || !best || best.gap >= MIN_GAP_DAYS) return { ok: true, nearest: best };
  return { ok: false, tooClose: true, other: best.dayKey, gap: best.gap };
}

/** The day as Planning needs it for the lists below. */
export interface SpacingDay {
  dayKey: string;
  open: boolean;
  /** Ink + pencil already on the day. */
  load: number;
  /** The day's base capacity (5). */
  base: number;
}

export interface GoodDay {
  dayKey: string;
  /** Visits already on the day (ink + pencil). */
  load: number;
  /** Days from the tenant's nearest other visit (null: they have none). */
  gap: number | null;
  /** Which side of the day that other visit is on (null: none). */
  side: 'after' | 'before' | null;
  /** The day already holds its base capacity: this would be the 6th visit — extra staff. */
  sixth: boolean;
  /** D-1104-NEEDS Q6: the day is the tenant's usual weekday (their Completed visits in the last 3 months). */
  usual?: boolean;
}

export interface GoodDays {
  /** Open, with room, spacing met — closest to the 14-day target first, then the most room. */
  good: GoodDay[];
  /** Open, spacing met, exactly full: the 6th visit with extra staff — after the good ones. */
  sixth: GoodDay[];
}

export interface GoodDaysInput {
  days: readonly SpacingDay[];
  /** The tenant's other visits, any month. */
  others: readonly OtherVisit[];
  twicePerMonth: boolean;
  /** Earliest day to offer (inclusive): today, or the first date plus 14. */
  from?: string;
  /** The visit being placed or moved — left out of the spacing. */
  excludeId?: string;
}

/**
 * Days a tenant could take their next visit: Service days that are open, spacing met, room under the base (5). Nearest
 * first = earliest first from `from`. Days holding exactly the base are listed apart as the 6th visit.
 */
export function goodDays(input: GoodDaysInput): GoodDays {
  const good: GoodDay[] = [];
  const sixth: GoodDay[] = [];
  for (const d of [...input.days].sort((a, b) => a.dayKey.localeCompare(b.dayKey))) {
    if (!d.open || (input.from && d.dayKey < input.from)) continue;
    if (input.others.some((o) => o.dayKey === d.dayKey && o.appointmentId !== input.excludeId)) continue;
    const sp = spacingOf(d.dayKey, input.others, input.twicePerMonth, input.excludeId);
    if (!sp.ok) continue;
    const near = sp.nearest;
    const row: GoodDay = { dayKey: d.dayKey, load: d.load, gap: near?.gap ?? null, side: near ? (near.dayKey < d.dayKey ? 'after' : 'before') : null, sixth: d.load === d.base };
    if (d.load < d.base) good.push(row);
    else if (d.load === d.base) sixth.push(row);
  }
  // Twice a month: closest to the 14-day target first, then the lighter day, then the earlier one. Otherwise earliest first.
  if (input.twicePerMonth) {
    const away = (g: GoodDay) => (g.gap === null ? 0 : Math.abs(g.gap - TARGET_GAP_DAYS));
    const byTarget = (a: GoodDay, b: GoodDay) => away(a) - away(b) || a.load - b.load || a.dayKey.localeCompare(b.dayKey);
    good.sort(byTarget);
    sixth.sort(byTarget);
  }
  return { good, sixth };
}

/** The first day a twice-a-month tenant's second visit may take after a first date (10 days on). */
export const earliestSecond = (firstDay: string): string => addDaysToKey(firstDay, MIN_GAP_DAYS);
/** The day the second visit aims for: the same weekday two weeks later. */
export const targetSecond = (firstDay: string): string => addDaysToKey(firstDay, TARGET_GAP_DAYS);

/* ---------------------------------------------------------------------------------------------- */
/* Does the month fit?                                                                              */
/* ---------------------------------------------------------------------------------------------- */

export interface FitPerson {
  /** Visits authorized this month (null = no fixed limit, counted as one). */
  authorized: number | null;
  /** Visits the tenant holds or has had this month. */
  held: number;
  /** Visits already done (or past) this month — used only when the fit counts from today. */
  done?: number;
}

export interface MonthFit {
  /** Visits needed this month: the sum of the authorized visits of active enrollments. */
  needed: number;
  openDays: number;
  /** Open days at 5 a day. */
  room: number;
  /** 6th-visit days the month needs: max(0, needed − room). */
  sixthDays: number;
  /** People still short of their visits. */
  short: number;
  /** True when the count starts at today (the month has begun): needed = visits not yet done, room = open days from today. */
  fromToday: boolean;
}

/**
 * `fromKey` set (D-1019-PLAN, 19 Oct: the month has started): needed = the visits not yet done, room = open working days
 * from `fromKey` (today, inclusive) to the month's end at the base a day. Unset: the whole month, as before.
 */
export function monthFit(people: readonly FitPerson[], days: readonly { open: boolean; base: number; dayKey?: string }[], fromKey?: string): MonthFit {
  const open = days.filter((d) => d.open && (!fromKey || (d.dayKey ?? '') >= fromKey));
  const need = (p: FitPerson) => (p.authorized ?? 1) - (fromKey ? Math.min(p.done ?? 0, p.authorized ?? 1) : 0);
  const needed = people.reduce((n, p) => n + need(p), 0);
  const room = open.reduce((n, d) => n + d.base, 0);
  const short = people.filter((p) => p.held < (p.authorized ?? 1)).length;
  return { needed, openDays: open.length, room, sixthDays: Math.max(0, needed - room), short, fromToday: !!fromKey };
}

/* ---------------------------------------------------------------------------------------------- */
/* The plain verdict on "Does the month fit?" (D-1104-FIT)                                          */
/* ---------------------------------------------------------------------------------------------- */

/** "Only n open days left" shows when this few remain and the month does not simply fit. */
export const FEW_DAYS = 5;

export type FitState = 'fits' | 'tight' | 'no';

export interface FitDayIn {
  dayKey: string;
  open: boolean;
  base: number;
  /** 'blackout' | 'notOpen' | 'noSlot' (Planning's PlanningDay). */
  closedReason?: string;
  /** The blackout's name, e.g. "Thanksgiving". */
  blackout?: string | null;
}

export interface FitVerdict {
  state: FitState;
  /** Visits left to do (MonthFit.needed). */
  needed: number;
  openDays: number;
  /** Room at the base a day (5 on a normal day). */
  room5: number;
  /** Room at base + 1 a day (the processor's hard limit, with a crew of 4). */
  room6: number;
  /** Days that must take a 6th visit for the month to fit: max(0, needed − room5). */
  six: number;
  /** Visits with no day even at base + 1: max(0, needed − room6). */
  short: number;
  /** Few open days left (≤ FEW_DAYS) while the month does not simply fit. */
  fewDays: boolean;
  /** Weekdays from `fromKey` closed on purpose (a blackout or a closed Service day); weekends and "no Service day yet" are not here. */
  closed: { dayKey: string; name: string | null }[];
  /** Weekdays with no Service day created yet (not counted, not an alarm). */
  noSlot: number;
}

/**
 * Fits / Tight / Will not fit. `monthFit` is left as it was; this reads its answer and the days. 5 is each day's base,
 * 6 is base + 1 (the surge position). Pure.
 */
export function fitVerdict(fit: MonthFit, days: readonly FitDayIn[], fromKey?: string): FitVerdict {
  const from = (d: FitDayIn) => !fromKey || d.dayKey >= fromKey;
  const room6 = days.filter((d) => d.open && from(d)).reduce((n, d) => n + positionCount(d.base), 0);
  const needed = fit.needed;
  const room5 = fit.room;
  const state: FitState = needed <= room5 ? 'fits' : needed <= room6 ? 'tight' : 'no';
  const closed: FitVerdict['closed'] = [];
  let noSlot = 0;
  for (const d of days) {
    if (d.open || !from(d) || !isWeekdayKey(d.dayKey)) continue;
    if (d.closedReason === 'noSlot') noSlot += 1;
    else closed.push({ dayKey: d.dayKey, name: d.blackout ?? null });
  }
  closed.sort((a, b) => a.dayKey.localeCompare(b.dayKey));
  return {
    state,
    needed,
    openDays: fit.openDays,
    room5,
    room6,
    six: Math.max(0, needed - room5),
    short: Math.max(0, needed - room6),
    fewDays: fit.openDays <= FEW_DAYS && needed > 0 && state !== 'fits',
    closed,
    noSlot,
  };
}

/* ---------------------------------------------------------------------------------------------- */
/* A tenant, as Planning's worklist row has them                                                    */
/* ---------------------------------------------------------------------------------------------- */

export interface SpacingPerson {
  authorized: number | null;
  used: number;
  oldSystem?: number;
  /** The days they were seen this month (the app's completed visits and the old system's). */
  went?: string[];
  lines: readonly { appointmentId: string; dayKey: string; status?: string }[];
  /** Their live visits in the 13 days before and after the month. */
  neighbours?: string[];
  /** D-1104-NEEDS Q6: the weekday (1 Monday … 5 Friday) of most of their Completed visits in the last 3 months. The API leaves it out when HK_USUAL_DAY_FIRST is No. */
  usualDay?: number | null;
}

/** D-1104-NEEDS Q5 (HK_MISSED_BACK_ON_LIST): with it, a No Show or Unable to Complete this month is not a held visit. */
export interface HeldOptions {
  missedBack?: boolean;
}

/** Two visits a month. "No fixed limit" and one a month are not spaced. */
export const twicePerMonthOf = (p: Pick<SpacingPerson, 'authorized'>): boolean => p.authorized !== null && p.authorized >= 2;

/** Every visit of theirs that counts for spacing: this month's lines and visits had, and the edges of the months around. */
export function visitsOf(p: SpacingPerson): OtherVisit[] {
  const out = new Map<string, OtherVisit>();
  for (const k of [...(p.neighbours ?? []), ...(p.went ?? [])]) out.set(k, { dayKey: k });
  for (const l of p.lines) out.set(l.dayKey, { dayKey: l.dayKey, appointmentId: l.appointmentId });
  return [...out.values()].sort((a, b) => a.dayKey.localeCompare(b.dayKey));
}

/** Visits they hold or have had this month. */
export const visitsHeld = (p: SpacingPerson, o: HeldOptions = {}): number => {
  // The processor's own rule for both outcomes: "The slot was released and the visit does not count against the tenant's
  // approved visits" (processor/definition_q1_v8o.json). `used` already leaves them out; the lines must too.
  const lines = o.missedBack ? p.lines.filter((l) => !isMissedStatus(l.status)) : p.lines;
  return Math.max(p.used, lines.length + (p.oldSystem ?? 0));
};

/** Visits still without a date this month (no line, or a line moved off). Nobody is dropped: this is the list. */
export function stillNeeds(p: SpacingPerson, o: HeldOptions = {}): number {
  const held = visitsHeld(p, o);
  return p.authorized === null ? (held > 0 ? 0 : 1) : Math.max(0, p.authorized - held);
}

/**
 * The days to offer a tenant for a visit they still need: after their last visit this month by 10 or more days (closest to 14 first) when they have
 * one (the "second visit" list), else — or when nothing is left that way — any open day from today with the spacing met.
 */
export function nextGoodDays(p: SpacingPerson, days: readonly SpacingDay[], todayKey: string, monthStartKey: string, o: HeldOptions = {}): GoodDays & { after: string | null } {
  // a missed visit (when it is back on the list) is not a date they hold: its day is open to them again
  const mine: SpacingPerson = o.missedBack ? { ...p, lines: p.lines.filter((l) => !isMissedStatus(l.status)) } : p;
  p = mine;
  const others = visitsOf(p);
  const twice = twicePerMonthOf(p);
  const start = todayKey > monthStartKey ? todayKey : monthStartKey;
  const inMonth = others.filter((o) => o.dayKey >= monthStartKey && days.some((d) => d.dayKey === o.dayKey));
  const last = inMonth.length ? inMonth[inMonth.length - 1]!.dayKey : null;
  if (twice && last) {
    const from = earliestSecond(last) > start ? earliestSecond(last) : start;
    const r = goodDays({ days, others, twicePerMonth: twice, from });
    if (r.good.length || r.sixth.length) return { ...r, after: last };
  }
  return { ...withUsualDay(goodDays({ days, others, twicePerMonth: twice, from: start }), p.usualDay, twice), after: null };
}

/**
 * D-1104-NEEDS Q6: for a first visit, the tenant's usual weekday comes first when it has an open good day this month (same
 * spacing, room under the base). Twice a month: the lightest such day, then the earliest; once a month: the earliest.
 * No such day: the list is as it was.
 */
export function withUsualDay(r: GoodDays, usualDay: number | null | undefined, twice: boolean): GoodDays {
  if (!usualDay) return r;
  const mine = r.good.filter((g) => dayOfWeek(g.dayKey) === usualDay).map((g) => ({ ...g, usual: true }));
  if (!mine.length) return r;
  mine.sort((a, b) => (twice ? a.load - b.load : 0) || a.dayKey.localeCompare(b.dayKey));
  return { good: [...mine, ...r.good.filter((g) => dayOfWeek(g.dayKey) !== usualDay)], sixth: r.sixth };
}
